import type { ColumnDef } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { Clubhouse_LeagueV2RosterMemberDto } from "../../types/leagueRoster";
import { TeeSwatch } from "./TeeBadge";
import type { DataTableColumnMeta } from "./DataTable";

/**
 * The table is only as wide as its columns. Every column is sized, so the card
 * shrink-wraps them (`fitContent`) rather than stretching the handicap away
 * from the name across an empty page.
 *
 * Widths include their own padding (border-box), so they are the content width
 * plus 18px at the table's two outer edges and 16px between columns — which is
 * what adds up to the 512px and 654px cards in the mockups. `PAD` carries the
 * matching classes: an outer edge on the side that faces one.
 */
const PAD = {
  first: "pl-4.5 pr-2",
  middle: "px-2",
  last: "pl-2 pr-4.5",
} as const;
const INDIVIDUAL_WIDTHS = { name: 286, handicap: 126, actions: 100 } as const;
const INDIVIDUAL_WIDTHS_NO_HANDICAP = { name: 412, actions: 100 } as const;
const TEAM_WIDTHS = {
  name: 256,
  team: 206,
  handicap: 126,
  actions: 66,
} as const;
const TEAM_WIDTHS_NO_HANDICAP = { name: 382, team: 206, actions: 66 } as const;

/** 62px rows, and a header shorter than its cells. */
export const ROSTER_ROW_CLASS = "h-[62px] [&>td]:py-0";
export const ROSTER_HEADER_CLASS = "h-10 py-0 bg-surface text-[10.5px]";

/** Soft green pill for an established aG Handicap. Starting values stay plain. */
const handicapStyles = {
  pill: "monoNumber inline-flex min-w-12 items-start justify-center rounded-full bg-text-figure/15 px-2.5 py-0.5 text-xs tabular-nums text-text-figure dark:bg-emerald-400/15 dark:text-emerald-200",
  pillButton:
    "cursor-pointer transition-colors hover:bg-emerald-200/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600/40 dark:hover:bg-emerald-400/25",
  value: "monoNumber text-[11px] font-extrabold tabular-nums text-text-figure",
  startingTag:
    "poppins rounded-md bg-muted px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.06em] text-text-muted",
} as const;

/**
 * The handicap this player's rounds will score against.
 *
 * One column, not two. An established aG Handicap is a soft green pill:
 * clicking it opens that player's handicap history, and hovering names the
 * action. A starting handicap is plain text with a Starting tag — it is not
 * a pill and not clickable, because Round 1 has not set an aG Handicap yet.
 * A player with neither gets a quiet dash: a roster that has just gained a
 * player is not in an error state, and flagging it as one trains the
 * operator to ignore the flag.
 *
 * The aG handicap is calculated after the first league round and applied
 * retroactively; nothing on this screen drives that.
 */
function HandicapCell({
  member,
  onViewHistory,
}: {
  member: Clubhouse_LeagueV2RosterMemberDto;
  onViewHistory?: (member: Clubhouse_LeagueV2RosterMemberDto) => void;
}) {
  const established = member.currentHandicap;
  const starting = member.seedHandicap;

  if (established != null) {
    const label = established.toFixed(1);
    if (!onViewHistory) {
      return <span className={handicapStyles.pill}>{label}</span>;
    }
    const name = member.playerName ?? "player";
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                className={cn(handicapStyles.pill, handicapStyles.pillButton)}
                aria-label={`View handicap history for ${name}`}
                onClick={() => onViewHistory(member)}
              />
            }
          >
            {label}
          </TooltipTrigger>
          <TooltipContent>View handicap history</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (starting == null) {
    return <span className="text-sm text-text-muted">—</span>;
  }

  return (
    <span className="flex flex-col items-center gap-0.5">
      <span className={handicapStyles.value}>{starting.toFixed(1)}</span>
      <span className={handicapStyles.startingTag}>Starting</span>
    </span>
  );
}

export interface RosterColumnOptions {
  onEdit: (member: Clubhouse_LeagueV2RosterMemberDto) => void;
  onRemove: (member: Clubhouse_LeagueV2RosterMemberDto) => void;
  canEdit?: boolean;
  onViewHandicapHistory?: (member: Clubhouse_LeagueV2RosterMemberDto) => void;
  removal?: "remove" | "withdraw" | "none";
  /**
   * Team name by roster `teamId`. Supplying it adds the Team column, which only
   * a team league has. Ids are slugs on both sides, so they are keyed as strings.
   */
  teamNameById?: Map<string, string>;
  /** Scramble leagues score as a team; per-player handicap is not shown here. */
  hideHandicapColumn?: boolean;
}

export function getRosterColumns({
  onEdit,
  onRemove,
  canEdit = true,
  onViewHandicapHistory,
  removal = "remove",
  teamNameById,
  hideHandicapColumn = false,
}: RosterColumnOptions): ColumnDef<Clubhouse_LeagueV2RosterMemberDto>[] {
  const lockedTitle =
    "The roster can only be changed while the league is a draft or active; it locks once archived";
  const widths = teamNameById
    ? hideHandicapColumn
      ? TEAM_WIDTHS_NO_HANDICAP
      : TEAM_WIDTHS
    : hideHandicapColumn
      ? INDIVIDUAL_WIDTHS_NO_HANDICAP
      : INDIVIDUAL_WIDTHS;

  const teamColumn: ColumnDef<Clubhouse_LeagueV2RosterMemberDto> = {
    id: "team",
    accessorFn: (member) =>
      teamNameById?.get(String(member.teamId ?? "")) ?? "",
    header: "Team",
    meta: {
      className: `min-w-0 ${PAD.middle}`,
      headerClassName: ROSTER_HEADER_CLASS,
      width: TEAM_WIDTHS.team,
    } satisfies DataTableColumnMeta,
    cell: ({ row }) => {
      const name = teamNameById?.get(String(row.original.teamId ?? ""));
      return name ? (
        <span
          className="poppins block truncate text-[13px] font-bold text-text-figure"
          title={name}
        >
          {name}
        </span>
      ) : (
        <span className="mulish text-xs italic text-text-figure">
          Unassigned
        </span>
      );
    },
  };

  return [
    {
      accessorKey: "playerName",
      header: "Name",
      cell: ({ row }) => (
        <span className="flex flex-col gap-0.5">
          <span
            className="poppins block truncate text-sm font-extrabold text-text-figure"
            title={row.original.playerName ?? undefined}
          >
            {row.original.playerName ?? "—"}
          </span>
          {/* The tee, where the Scorecard screen also puts it: under the
              name, as a dot and a label. Every round applies its own default
              tee to every participant, so this is here to be seen rather
              than filled in — which is why it is no longer a column of its
              own, and why an unset tee is not treated as missing data. It
              stays editable through the row's Edit action. */}
          <TeeSwatch teeType={row.original.teeType} />
        </span>
      ),
      meta: {
        className: `min-w-0 ${PAD.first}`,
        headerClassName: ROSTER_HEADER_CLASS,
        width: widths.name,
      } satisfies DataTableColumnMeta,
    },
    ...(teamNameById ? [teamColumn] : []),
    ...(!hideHandicapColumn
      ? [
          {
            accessorKey: "currentHandicap",
            // Not "aG Handicap": a player can have a starting handicap and no aG
            // Handicap yet, and this column holds whichever one applies.
            header: "Handicap",
            // Fixed, not a share of the row: the "Starting" tag makes one row's
            // cell wider than a bare number, and a content-sized column would let
            // that row set a width the rest of the column does not share.
            meta: {
              className: `text-center ${PAD.middle}`,
              headerClassName: ROSTER_HEADER_CLASS,
              width: TEAM_WIDTHS.handicap,
            } satisfies DataTableColumnMeta,
            cell: ({ row }) => (
              <HandicapCell
                member={row.original}
                onViewHistory={onViewHandicapHistory}
              />
            ),
          } as ColumnDef<Clubhouse_LeagueV2RosterMemberDto>,
        ]
      : []),
    {
      id: "actions",
      header: "",
      meta: {
        className: `text-center ${PAD.last}`,
        headerClassName: ROSTER_HEADER_CLASS,
        width: widths.actions,
      } satisfies DataTableColumnMeta,
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex items-center justify-center gap-0.5">
          {/* Edit stays a pencil. Remove is a trash can beside it — a soft
              delete — rather than an item in an overflow menu. Handicap
              history lives on the handicap pill, not in this pair. A team
              league shows no trash at all: a whole team is withdrawn from the
              Teams view, and a player who leaves is covered by a substitute. */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="size-8 rounded-md p-0"
            disabled={!canEdit}
            title={canEdit ? "Edit player" : lockedTitle}
            onClick={() => onEdit(row.original)}
          >
            <Pencil data-icon />
            <span className="sr-only">
              Edit {row.original.playerName ?? "player"}
            </span>
          </Button>
          {removal !== "none" && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-8 rounded-md p-0"
              disabled={!canEdit}
              title={
                canEdit
                  ? removal === "withdraw"
                    ? "Withdraw player"
                    : "Remove player"
                  : lockedTitle
              }
              onClick={() => onRemove(row.original)}
            >
              <Trash2 data-icon />
              <span className="sr-only">
                {removal === "withdraw" ? "Withdraw" : "Remove"}{" "}
                {row.original.playerName ?? "player"}
              </span>
            </Button>
          )}
        </div>
      ),
    },
  ];
}
